import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatINRFull, getStatusColor, formatStatus, LEAD_STATUSES, formatDate, formatDateTime, LEAD_CATEGORIES, BOQ_CATEGORIES } from '@/lib/utils';
import { ArrowLeft, Plus, Edit2, Trash2, Clock, Save, ChevronRight, FileText, Download, Upload, X, Eye, Settings } from 'lucide-react';

export default function LeadDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [clients, setClients] = useState([]);

  const fetchLead = () => {
    api.get(`/sales/${id}`).then(r => { setLead(r.data.lead); setEditForm(r.data.lead); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLead(); api.get('/clients').then(r => setClients(r.data.clients || [])); }, [id]);

  const canEdit = ['DIRECTOR', 'SALES'].includes(user?.role);

  const updateLead = async () => {
    try {
      await api.patch(`/sales/${id}`, editForm);
      fetchLead();
      setEditMode(false);
    } catch (err) { alert(err.response?.data?.detail || 'Update failed'); }
  };

  const convertToProject = async () => {
    if (!window.confirm('Convert this lead to a project? This will copy BOQ items and create a new project.')) return;
    try {
      const res = await api.post(`/sales/${id}/convert`);
      navigate(`/projects/${res.data.project.id}`);
    } catch (err) { alert(err.response?.data?.detail || 'Conversion failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>;
  if (!lead) return <div className="text-center py-12 text-slate-500">Lead not found</div>;

  return (
    <div data-testid="lead-detail-page" className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate('/sales')} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900 font-heading">{lead.lead_code}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(lead.status)}`}>{formatStatus(lead.status)}</span>
          </div>
          <p className="text-sm text-slate-500">{lead.project_requirement}</p>
        </div>
        <div className="flex gap-2">
          {canEdit && lead.status !== 'WON' && !lead.project_id && (
            <button onClick={convertToProject} data-testid="convert-to-project-btn"
              className="bg-emerald-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-emerald-700 flex items-center gap-1">
              <ChevronRight size={16} /> Convert to Project
            </button>
          )}
          {lead.project_id && (
            <Link to={`/projects/${lead.project_id}`} className="bg-[#1B5486] text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-1">
              View Project <ChevronRight size={16} />
            </Link>
          )}
        </div>
      </div>

      <div className="flex border-b border-slate-200 gap-1">
        {['details', 'boq', 'timeline'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t === 'details' ? 'Details' : t === 'boq' ? 'BOQ' : 'Timeline'}
          </button>
        ))}
      </div>

      {tab === 'details' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Lead Information</h3>
            {canEdit && !editMode && <button onClick={() => setEditMode(true)} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Edit2 size={14} /> Edit</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Client" value={lead.client?.name || ''} editing={editMode}
              editComponent={<select value={editForm.client_id} onChange={e => setEditForm({...editForm, client_id: e.target.value})} className="input-field">
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>} />
            <Field label="Department" value={lead.department} editing={editMode}
              editComponent={<input value={editForm.department || ''} onChange={e => setEditForm({...editForm, department: e.target.value})} className="input-field" />} />
            <Field label="Category" value={formatStatus(lead.category)} editing={editMode}
              editComponent={<select value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})} className="input-field">
                {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
              </select>} />
            <Field label="Status" value={<span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(lead.status)}`}>{formatStatus(lead.status)}</span>} editing={editMode}
              editComponent={<select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})} className="input-field">
                {LEAD_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
              </select>} />
            <Field label="Contact Name" value={lead.initial_contact_name} editing={editMode}
              editComponent={<input value={editForm.initial_contact_name || ''} onChange={e => setEditForm({...editForm, initial_contact_name: e.target.value})} className="input-field" />} />
            <Field label="Contact Date" value={formatDate(lead.initial_contact_date)} />
            <Field label="Quoted Amount" value={
              <span className="flex items-center gap-1.5">
                {formatINRFull(lead.initial_quoted_amount)}
                {(lead.boq_items || []).length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">BOQ Synced</span>}
              </span>
            } editing={editMode}
              editComponent={<div>
                <input type="number" value={editForm.initial_quoted_amount || ''} onChange={e => {
                const iq = parseFloat(e.target.value) || 0;
                const fo = parseFloat(editForm.final_offer) || 0;
                const dp = iq > 0 && fo > 0 ? (((iq - fo) / iq) * 100).toFixed(2) : '';
                setEditForm({...editForm, initial_quoted_amount: e.target.value, discount_percent: dp});
              }} className="input-field" />
                {(lead.boq_items || []).length > 0 && <p className="text-[10px] text-blue-500 mt-0.5">Auto-synced from BOQ Client Total when BOQ changes</p>}
              </div>} />
            <Field label="Discount %" value={lead.discount_percent ? `${lead.discount_percent}%` : '-'} editing={editMode}
              editComponent={<input type="number" value={editForm.discount_percent || ''} readOnly className="input-field bg-slate-50 cursor-not-allowed" title="Auto-calculated from Quoted Amount and Final Offer" />} />
            <Field label="Final Offer" value={formatINRFull(lead.final_offer)} editing={editMode}
              editComponent={<input type="number" value={editForm.final_offer || ''} onChange={e => {
                const fo = parseFloat(e.target.value) || 0;
                const iq = parseFloat(editForm.initial_quoted_amount) || 0;
                const dp = iq > 0 && fo > 0 ? (((iq - fo) / iq) * 100).toFixed(2) : '';
                setEditForm({...editForm, final_offer: e.target.value, discount_percent: dp});
              }} className="input-field" />} />
            {['DIRECTOR', 'SALES'].includes(user?.role) && (
              <Field label="Cost to Company" value={
                <span className="flex items-center gap-1.5">
                  {formatINRFull(lead.cost_to_company)}
                  {(lead.boq_items || []).length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">BOQ Synced</span>}
                </span>
              } editing={editMode}
                editComponent={<div>
                  <input type="number" value={editForm.cost_to_company || ''} onChange={e => setEditForm({...editForm, cost_to_company: e.target.value})} className="input-field" />
                  {(lead.boq_items || []).length > 0 && <p className="text-[10px] text-amber-500 mt-0.5">Auto-synced from BOQ Pace Total when BOQ changes</p>}
                </div>} />
            )}
            <Field label="Purchaser" value={lead.purchaser_name} editing={editMode}
              editComponent={<input value={editForm.purchaser_name || ''} onChange={e => setEditForm({...editForm, purchaser_name: e.target.value})} className="input-field" />} />
            <Field label="Vendors" value={lead.vendor_names} editing={editMode}
              editComponent={<input value={editForm.vendor_names || ''} onChange={e => setEditForm({...editForm, vendor_names: e.target.value})} className="input-field" />} />
          </div>
          {editMode && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <button onClick={() => setEditMode(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={updateLead} data-testid="save-lead-btn" className="px-4 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-1"><Save size={14} /> Save</button>
            </div>
          )}
        </div>
      )}

      {tab === 'boq' && <BOQTab leadId={id} items={lead.boq_items || []} canEdit={canEdit} onUpdate={fetchLead} lead={lead} />}
      {tab === 'timeline' && <TimelineTab leadId={id} activities={lead.activities || []} canEdit={canEdit} onUpdate={fetchLead} />}
    </div>
  );
}

function Field({ label, value, editing, editComponent }) {
  return (
    <div>
      <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</span>
      <div className="mt-1">{editing && editComponent ? editComponent : <p className="text-sm text-slate-900 break-all">{value || '-'}</p>}</div>
    </div>
  );
}

function BOQTab({ leadId, items, canEdit, onUpdate, lead }) {
  const [showAdd, setShowAdd] = useState(false);
  const [showQuotationDialog, setShowQuotationDialog] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ sl_no: items.length + 1, item_name: '', description: '', make: '', model: '', units: '', uom: 'Nos', unit_cost_client: '', unit_cost_pace: '', category: 'EQUIPMENT', remarks: '' });

  const addItem = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/sales/${leadId}/boq`, { items: [form] });
      setShowAdd(false);
      setForm({ sl_no: items.length + 2, item_name: '', description: '', make: '', model: '', units: '', uom: 'Nos', unit_cost_client: '', unit_cost_pace: '', category: 'EQUIPMENT', remarks: '' });
      onUpdate();
    } catch (err) { alert('Failed to add BOQ item'); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this BOQ item?')) return;
    await api.delete(`/sales/${leadId}/boq/${itemId}`);
    onUpdate();
  };

  const saveEdit = async () => {
    if (!editItem) return;
    try {
      await api.put(`/sales/${leadId}/boq/${editItem.id}`, editItem);
      setEditItem(null);
      onUpdate();
    } catch (err) { alert('Failed to update BOQ item'); }
  };

  const downloadCSV = async () => {
    try {
      const res = await api.get(`/utility/export/boq/${leadId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `boq_export.csv`; a.click();
    } catch (err) { alert('Export failed'); }
  };

  const totalClient = items.reduce((s, i) => s + (i.total_cost_client || 0), 0);
  const totalPace = items.reduce((s, i) => s + (i.total_cost_pace || 0), 0);
  const totalMargin = totalClient - totalPace;
  const marginPct = totalClient > 0 ? (totalMargin / totalClient * 100) : 0;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="boq-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Bill of Quantities</h3>
        <div className="flex gap-2">
          <button onClick={() => setShowQuotationDialog(true)} data-testid="download-quotation-pdf"
            className="text-sm text-amber-600 hover:underline flex items-center gap-1"><FileText size={14} /> Quotation PDF</button>
          <button onClick={downloadCSV} data-testid="export-boq-csv" className="text-sm text-emerald-600 hover:underline flex items-center gap-1"><Download size={14} /> CSV</button>
          {canEdit && <button onClick={() => setShowImportWizard(true)} data-testid="import-boq-excel-btn" className="text-sm text-purple-600 hover:underline flex items-center gap-1"><Upload size={14} /> Import Excel</button>}
          {canEdit && <button onClick={() => setShowAdd(!showAdd)} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Plus size={14} /> Add Item</button>}
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addItem} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="add-boq-form">
          <input type="number" placeholder="S.No" value={form.sl_no} onChange={e => setForm({...form, sl_no: e.target.value})} required className="input-field" />
          <input placeholder="Item Name *" value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})} required className="input-field" data-testid="boq-item-name" />
          <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input-field col-span-2" />
          <input placeholder="Make" value={form.make} onChange={e => setForm({...form, make: e.target.value})} className="input-field" data-testid="boq-make" />
          <input placeholder="Model" value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="input-field" data-testid="boq-model" />
          <input type="number" placeholder="Qty" value={form.units} onChange={e => setForm({...form, units: e.target.value})} required className="input-field" />
          <select value={form.uom} onChange={e => setForm({...form, uom: e.target.value})} className="input-field">
            {['Nos', 'Mtrs', 'Box', 'Set', 'LS', 'Lot', 'Kg', 'Sqm', 'Rmt'].map(u => <option key={u}>{u}</option>)}
          </select>
          <input type="number" placeholder="Client Unit Cost" value={form.unit_cost_client} onChange={e => setForm({...form, unit_cost_client: e.target.value})} required className="input-field" />
          <input type="number" placeholder="Pace Unit Cost" value={form.unit_cost_pace} onChange={e => setForm({...form, unit_cost_pace: e.target.value})} required className="input-field" />
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field">
            {BOQ_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="submit" data-testid="add-boq-submit" className="bg-[#1B5486] text-white px-3 py-2 rounded-md text-sm">Add</button>
            <button type="button" onClick={() => setShowAdd(false)} className="border border-slate-200 px-3 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">S.No</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Item Name</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Description</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Make/Model</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Qty</th>
              <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">UOM</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-blue-50">Client Rate</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-blue-50">Client Total</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-amber-50">Pace Rate</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-amber-50">Pace Total</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-green-50">Margin</th>
              <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-green-50">%</th>
              {canEdit && <th className="w-16"></th>}
            </tr>
          </thead>
          <tbody>
            {items.map(item => editItem?.id === item.id ? (
              <tr key={item.id} className="border-b border-blue-100 bg-blue-50/30">
                <td className="py-1 px-2"><input type="number" value={editItem.sl_no} onChange={e => setEditItem({...editItem, sl_no: +e.target.value})} className="input-field w-14 text-xs" /></td>
                <td className="py-1 px-2"><input value={editItem.item_name || ''} onChange={e => setEditItem({...editItem, item_name: e.target.value})} className="input-field w-full text-xs" /></td>
                <td className="py-1 px-2"><input value={editItem.description || ''} onChange={e => setEditItem({...editItem, description: e.target.value})} className="input-field w-full text-xs" /></td>
                <td className="py-1 px-2"><input value={[editItem.make || '', editItem.model || ''].join(' / ')} onChange={e => { const [m, mo] = e.target.value.split(' / '); setEditItem({...editItem, make: m || '', model: mo || ''}); }} className="input-field w-full text-xs" /></td>
                <td className="py-1 px-2"><input type="number" value={editItem.units} onChange={e => setEditItem({...editItem, units: +e.target.value})} className="input-field w-16 text-xs text-right" /></td>
                <td className="py-1 px-2"><select value={editItem.uom} onChange={e => setEditItem({...editItem, uom: e.target.value})} className="input-field text-xs">{['Nos', 'Mtrs', 'Box', 'Set', 'LS', 'Lot', 'Kg', 'Sqm', 'Rmt'].map(u => <option key={u}>{u}</option>)}</select></td>
                <td className="py-1 px-2 bg-blue-50/50"><input type="number" value={editItem.unit_cost_client} onChange={e => setEditItem({...editItem, unit_cost_client: +e.target.value})} className="input-field w-20 text-xs text-right" /></td>
                <td className="py-1 px-2 bg-blue-50/50 font-medium text-right text-xs">{formatINRFull((editItem.units || 0) * (editItem.unit_cost_client || 0))}</td>
                <td className="py-1 px-2 bg-amber-50/50"><input type="number" value={editItem.unit_cost_pace} onChange={e => setEditItem({...editItem, unit_cost_pace: +e.target.value})} className="input-field w-20 text-xs text-right" /></td>
                <td className="py-1 px-2 bg-amber-50/50 font-medium text-right text-xs">{formatINRFull((editItem.units || 0) * (editItem.unit_cost_pace || 0))}</td>
                <td className="py-1 px-2 bg-green-50/50 text-right text-xs">{formatINRFull(((editItem.units || 0) * (editItem.unit_cost_client || 0)) - ((editItem.units || 0) * (editItem.unit_cost_pace || 0)))}</td>
                <td className="py-1 px-2 bg-green-50/50 text-right text-xs">-</td>
                <td className="py-1 px-2">
                  <div className="flex gap-1">
                    <button onClick={saveEdit} data-testid={`boq-save-${item.id}`} className="text-emerald-600 hover:text-emerald-800"><Save size={14} /></button>
                    <button onClick={() => setEditItem(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2 px-2 text-slate-500">{item.sl_no}</td>
                <td className="py-2 px-2 text-slate-900 font-medium max-w-[120px] truncate">{item.item_name || '-'}</td>
                <td className="py-2 px-2 text-slate-700 max-w-[150px] truncate">{item.description || '-'}</td>
                <td className="py-2 px-2 text-slate-500 text-xs">{[item.make, item.model].filter(Boolean).join(' / ') || '-'}</td>
                <td className="py-2 px-2 text-right">{item.units}</td>
                <td className="py-2 px-2 text-slate-500">{item.uom}</td>
                <td className="py-2 px-2 text-right bg-blue-50/50">{formatINRFull(item.unit_cost_client)}</td>
                <td className="py-2 px-2 text-right bg-blue-50/50 font-medium">{formatINRFull(item.total_cost_client)}</td>
                <td className="py-2 px-2 text-right bg-amber-50/50">{formatINRFull(item.unit_cost_pace)}</td>
                <td className="py-2 px-2 text-right bg-amber-50/50 font-medium">{formatINRFull(item.total_cost_pace)}</td>
                <td className={`py-2 px-2 text-right bg-green-50/50 font-medium ${item.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatINRFull(item.margin)}</td>
                <td className={`py-2 px-2 text-right bg-green-50/50 ${item.margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{item.margin_percent?.toFixed(1)}%</td>
                {canEdit && <td className="py-2 px-2">
                  <div className="flex gap-1">
                    <button onClick={() => setEditItem({...item})} data-testid={`boq-edit-${item.id}`} className="text-slate-300 hover:text-[#1B5486]"><Edit2 size={14} /></button>
                    <button onClick={() => deleteItem(item.id)} data-testid={`boq-delete-${item.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 font-semibold">
              <td colSpan={6} className="py-2 px-2 text-slate-900">TOTAL</td>
              <td className="py-2 px-2"></td>
              <td className="py-2 px-2 text-right bg-blue-50/50 text-[#1B5486]">{formatINRFull(totalClient)}</td>
              <td className="py-2 px-2"></td>
              <td className="py-2 px-2 text-right bg-amber-50/50 text-amber-700">{formatINRFull(totalPace)}</td>
              <td className={`py-2 px-2 text-right bg-green-50/50 ${totalMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatINRFull(totalMargin)}</td>
              <td className={`py-2 px-2 text-right bg-green-50/50 ${marginPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{marginPct.toFixed(1)}%</td>
              {canEdit && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {showQuotationDialog && (
        <QuotationDialog leadId={leadId} items={items} lead={lead} onClose={() => setShowQuotationDialog(false)} />
      )}
      {showImportWizard && (
        <BOQImportWizard leadId={leadId} onClose={() => setShowImportWizard(false)} onImported={() => { setShowImportWizard(false); onUpdate(); }} />
      )}
    </div>
  );
}

// BOQ-01: Excel Import Wizard with column mapping
function BOQImportWizard({ leadId, onClose, onImported }) {
  const BOQ_FIELDS = [
    { key: 'sl_no', label: 'S.No', required: false },
    { key: 'item_name', label: 'Item Name', required: true },
    { key: 'description', label: 'Description', required: false },
    { key: 'make', label: 'Make', required: false },
    { key: 'model', label: 'Model', required: false },
    { key: 'units', label: 'Quantity', required: true },
    { key: 'uom', label: 'UOM', required: false },
    { key: 'unit_cost_client', label: 'Client Unit Cost', required: true },
    { key: 'unit_cost_pace', label: 'Pace Unit Cost', required: false },
    { key: 'category', label: 'Category', required: false },
  ];

  const [step, setStep] = useState('upload'); // upload | map | preview
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileRows, setFileRows] = useState([]);
  const [colMap, setColMap] = useState({});
  const [mappedRows, setMappedRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/utility/preview/boq-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFileHeaders(res.data.headers || []);
      setFileRows(res.data.rows || []);
      // Auto-detect column mapping
      const autoMap = {};
      (res.data.headers || []).forEach(h => {
        const hl = h.toLowerCase().trim();
        if (hl.includes('sl') || hl.includes('s.no') || hl === 'sno') autoMap.sl_no = h;
        else if (hl.includes('item') && (hl.includes('name') || hl.includes('desc'))) autoMap.item_name = h;
        else if (hl.includes('desc') && !autoMap.item_name) autoMap.description = h;
        else if (hl === 'make' || hl.includes('brand')) autoMap.make = h;
        else if (hl === 'model') autoMap.model = h;
        else if (hl.includes('qty') || hl.includes('quantity') || hl === 'units' || hl === 'nos') autoMap.units = h;
        else if (hl === 'uom' || hl === 'unit') autoMap.uom = h;
        else if ((hl.includes('client') || hl.includes('selling') || hl.includes('rate') || hl.includes('price')) && !autoMap.unit_cost_client) autoMap.unit_cost_client = h;
        else if ((hl.includes('pace') || hl.includes('cost') || hl.includes('purchase')) && !autoMap.unit_cost_pace) autoMap.unit_cost_pace = h;
        else if (hl.includes('cat')) autoMap.category = h;
      });
      // If no item_name found but description exists, use description
      if (!autoMap.item_name && autoMap.description) {
        autoMap.item_name = autoMap.description;
        delete autoMap.description;
      }
      setColMap(autoMap);
      setStep('map');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to parse file'); }
    setUploading(false);
    e.target.value = '';
  };

  const applyMapping = () => {
    const rows = fileRows.map((row, idx) => {
      const mapped = {};
      BOQ_FIELDS.forEach(f => {
        const sourceCol = colMap[f.key];
        mapped[f.key] = sourceCol ? (row[sourceCol] || '') : '';
      });
      // Auto-assign sl_no if not mapped
      if (!mapped.sl_no) mapped.sl_no = String(idx + 1);
      return mapped;
    }).filter(r => r.item_name || r.description);
    setMappedRows(rows);
    setStep('preview');
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const payload = mappedRows.map((r, i) => ({
        ...r,
        sl_no: parseInt(r.sl_no) || (i + 1),
        units: parseFloat(r.units) || 0,
        unit_cost_client: parseFloat(r.unit_cost_client) || 0,
        unit_cost_pace: parseFloat(r.unit_cost_pace) || 0,
        uom: r.uom || 'Nos',
        category: r.category || 'EQUIPMENT',
      }));
      await api.post(`/utility/import/boq/${leadId}`, { rows: payload }, { headers: { 'Content-Type': 'application/json' } });
      onImported();
    } catch (err) { alert(err.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="boq-import-wizard">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900 font-heading">Import BOQ from Excel</h2>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 text-xs">
              {['upload', 'map', 'preview'].map((s, i) => (
                <span key={s} className={`px-2 py-0.5 rounded-full ${step === s ? 'bg-[#1B5486] text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
                </span>
              ))}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Upload size={48} className="text-slate-300 mb-4" />
              <p className="text-sm text-slate-600 mb-2">Upload an Excel (.xlsx) or CSV file</p>
              <p className="text-xs text-slate-400 mb-6">Merged cells will be expanded. Blank rows will be skipped.</p>
              <label className="bg-[#1B5486] text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-[#144066] cursor-pointer flex items-center gap-2">
                <Upload size={16} /> {uploading ? 'Parsing...' : 'Choose File'}
                <input type="file" accept=".xlsx,.csv" onChange={handleFileUpload} className="hidden" data-testid="boq-import-file-input" />
              </label>
            </div>
          )}

          {step === 'map' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Column Mapping</h3>
                <p className="text-xs text-slate-500 mb-3">Map your file columns to BOQ fields. Auto-detected mappings are pre-filled.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {BOQ_FIELDS.map(f => (
                  <div key={f.key} className="flex items-center gap-2" data-testid={`map-field-${f.key}`}>
                    <span className={`text-xs w-36 shrink-0 ${f.required ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </span>
                    <select value={colMap[f.key] || ''} onChange={e => setColMap({...colMap, [f.key]: e.target.value})}
                      className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-xs outline-none" data-testid={`map-select-${f.key}`}>
                      <option value="">-- Skip --</option>
                      {fileHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Found {fileRows.length} data rows in file | {fileHeaders.length} columns detected</p>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Preview ({mappedRows.length} items)</h3>
                <span className="text-xs text-slate-400">Showing first 10 rows</span>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-2 px-2 text-left text-slate-500 font-semibold">S.No</th>
                    <th className="py-2 px-2 text-left text-slate-500 font-semibold">Item Name</th>
                    <th className="py-2 px-2 text-left text-slate-500 font-semibold">Description</th>
                    <th className="py-2 px-2 text-left text-slate-500 font-semibold">Make</th>
                    <th className="py-2 px-2 text-right text-slate-500 font-semibold">Qty</th>
                    <th className="py-2 px-2 text-left text-slate-500 font-semibold">UOM</th>
                    <th className="py-2 px-2 text-right text-slate-500 font-semibold">Client Cost</th>
                    <th className="py-2 px-2 text-right text-slate-500 font-semibold">Pace Cost</th>
                  </tr></thead>
                  <tbody>
                    {mappedRows.slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`preview-row-${i}`}>
                        <td className="py-1.5 px-2 text-slate-500">{r.sl_no}</td>
                        <td className="py-1.5 px-2 text-slate-900 font-medium max-w-[200px] truncate">{r.item_name}</td>
                        <td className="py-1.5 px-2 text-slate-600 max-w-[150px] truncate">{r.description}</td>
                        <td className="py-1.5 px-2 text-slate-500">{r.make}</td>
                        <td className="py-1.5 px-2 text-right">{r.units}</td>
                        <td className="py-1.5 px-2">{r.uom}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{r.unit_cost_client}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{r.unit_cost_pace}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mappedRows.length > 10 && <p className="text-xs text-slate-400">...and {mappedRows.length - 10} more rows</p>}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-between">
          <button onClick={step === 'upload' ? onClose : () => setStep(step === 'preview' ? 'map' : 'upload')}
            className="px-4 py-2 border border-slate-200 rounded-md text-sm text-slate-600 hover:bg-slate-50">
            {step === 'upload' ? 'Cancel' : 'Back'}
          </button>
          {step === 'map' && (
            <button onClick={applyMapping} data-testid="apply-mapping-btn"
              disabled={!colMap.item_name || !colMap.units || !colMap.unit_cost_client}
              className="px-5 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-50">
              Preview Items
            </button>
          )}
          {step === 'preview' && (
            <button onClick={confirmImport} disabled={importing} data-testid="confirm-import-btn"
              className="px-5 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
              {importing ? 'Importing...' : `Import ${mappedRows.length} Items`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// QOT-01 + QOT-02 + QOT-03: Quotation Generation Dialog
function QuotationDialog({ leadId, items, lead, onClose }) {
  const GST_RATES = [5, 12, 18, 28];
  const [defaultGst, setDefaultGst] = useState(18);
  const [customGst, setCustomGst] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [itemGstRates, setItemGstRates] = useState({});
  const [taxType, setTaxType] = useState('CGST_SGST');
  const [terms, setTerms] = useState([]);
  const [closingText, setClosingText] = useState('For PACE INFOCOM\nAuthorized Signatory');
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('config'); // 'config' | 'preview'

  // Load template defaults
  useEffect(() => {
    api.get('/quotation-templates').then(r => {
      const templates = r.data.templates || [];
      const def = templates.find(t => t.is_default) || templates[0];
      if (def) {
        setTerms(def.terms_and_conditions || []);
        if (def.closing_text) setClosingText(def.closing_text);
      } else {
        setTerms([
          'Goods once sold will not be taken back.',
          'Goods will be supplied within 4-6 weeks after receiving order.',
          'Price valid for 30 days only.',
          'Payment Terms AP-03, Delivery at Client Site.',
          'Any dispute considered Rudrapur jurisdiction only.',
          'All Prices exclusive of GST. GST Applicable as mentioned.',
          'Freight Inclusive.'
        ]);
      }
      // Load previously saved tax config
      if (lead?.tax_config) {
        const tc = lead.tax_config;
        if (tc.default_gst_rate) {
          if (GST_RATES.includes(tc.default_gst_rate)) {
            setDefaultGst(tc.default_gst_rate);
          } else {
            setUseCustom(true);
            setCustomGst(String(tc.default_gst_rate));
          }
        }
        if (tc.item_gst_rates) setItemGstRates(tc.item_gst_rates);
      }
    }).catch(() => {}).finally(() => setLoadingTemplate(false));
  }, []);

  const effectiveGst = useCustom ? (parseFloat(customGst) || 0) : defaultGst;

  const getItemGst = (itemId) => {
    return itemGstRates[itemId] !== undefined ? parseFloat(itemGstRates[itemId]) : effectiveGst;
  };

  // Calculate tax-wise totals for preview
  const taxGroups = {};
  items.forEach(item => {
    const rate = getItemGst(item.id);
    const tc = item.total_cost_client || 0;
    const gstAmt = Math.round(tc * rate / 100);
    if (!taxGroups[rate]) taxGroups[rate] = { taxable: 0, gst: 0 };
    taxGroups[rate].taxable += tc;
    taxGroups[rate].gst += gstAmt;
  });

  const buildPayload = () => ({
    default_gst_rate: effectiveGst,
    item_gst_rates: itemGstRates,
    tax_type: taxType,
    terms_and_conditions: terms,
    closing_text: closingText,
  });

  // QOT-03: Preview
  const handlePreview = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/utility/quotation/${leadId}`, buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setStep('preview');
    } catch (err) { alert('Preview generation failed'); }
    setGenerating(false);
  };

  // Download
  const handleDownload = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/utility/quotation/${leadId}`, buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Quotation_${lead?.lead_code || leadId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed'); }
    setGenerating(false);
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className={`bg-white rounded-lg border ${step === 'preview' ? 'w-full max-w-5xl h-[90vh]' : 'w-full max-w-3xl max-h-[90vh]'} overflow-hidden flex flex-col`}
        onClick={e => e.stopPropagation()} data-testid="quotation-dialog">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900 font-heading flex items-center gap-2">
            <Settings size={16} className="text-[#1B5486]" />
            {step === 'preview' ? 'Quotation Preview' : 'Configure Quotation'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {step === 'config' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {loadingTemplate ? <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
              <>
                {/* QOT-01: GST Configuration */}
                <div data-testid="gst-config-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Tax Configuration</h3>
                  <div className="flex flex-wrap gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Default GST Rate</label>
                      <div className="flex gap-1.5">
                        {GST_RATES.map(r => (
                          <button key={r} type="button" onClick={() => { setDefaultGst(r); setUseCustom(false); }}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${!useCustom && defaultGst === r ? 'bg-[#1B5486] text-white border-[#1B5486]' : 'border-slate-200 text-slate-600 hover:border-[#1B5486]'}`}
                            data-testid={`gst-rate-${r}`}>
                            {r}%
                          </button>
                        ))}
                        <button type="button" onClick={() => setUseCustom(true)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${useCustom ? 'bg-[#1B5486] text-white border-[#1B5486]' : 'border-slate-200 text-slate-600 hover:border-[#1B5486]'}`}
                          data-testid="gst-rate-custom">
                          Custom
                        </button>
                      </div>
                      {useCustom && (
                        <input type="number" value={customGst} onChange={e => setCustomGst(e.target.value)}
                          placeholder="Enter %" className="input-field mt-2 w-32" data-testid="gst-custom-input" />
                      )}
                    </div>
                  </div>

                  {/* Per-item GST overrides */}
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-xs font-medium text-slate-600">Tax Type:</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="taxType" value="CGST_SGST" checked={taxType === 'CGST_SGST'} onChange={() => setTaxType('CGST_SGST')} className="accent-[#1B5486]" />
                      <span className="text-xs text-slate-700">Intra-State (CGST + SGST)</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="taxType" value="IGST" checked={taxType === 'IGST'} onChange={() => setTaxType('IGST')} className="accent-[#1B5486]" data-testid="igst-toggle" />
                      <span className="text-xs text-slate-700">Inter-State (IGST)</span>
                    </label>
                  </div>

                  {/* Per-item GST table */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left py-2 px-3 font-semibold text-slate-500">S.No</th>
                          <th className="text-left py-2 px-3 font-semibold text-slate-500">Item</th>
                          <th className="text-right py-2 px-3 font-semibold text-slate-500">Amount</th>
                          <th className="text-right py-2 px-3 font-semibold text-slate-500 w-28">GST %</th>
                          <th className="text-right py-2 px-3 font-semibold text-slate-500">GST Amt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => {
                          const rate = getItemGst(item.id);
                          const gstAmt = Math.round((item.total_cost_client || 0) * rate / 100);
                          return (
                            <tr key={item.id} className="border-b border-slate-50" data-testid={`gst-item-row-${item.id}`}>
                              <td className="py-1.5 px-3 text-slate-500">{item.sl_no}</td>
                              <td className="py-1.5 px-3 text-slate-900 font-medium truncate max-w-[200px]">{item.item_name}</td>
                              <td className="py-1.5 px-3 text-right font-mono">{formatINRFull(item.total_cost_client)}</td>
                              <td className="py-1.5 px-3 text-right">
                                <select value={itemGstRates[item.id] !== undefined ? itemGstRates[item.id] : ''}
                                  onChange={e => {
                                    const v = e.target.value;
                                    if (v === '') {
                                      const newRates = {...itemGstRates};
                                      delete newRates[item.id];
                                      setItemGstRates(newRates);
                                    } else {
                                      setItemGstRates({...itemGstRates, [item.id]: parseFloat(v)});
                                    }
                                  }}
                                  className="border border-slate-200 rounded px-2 py-1 text-xs w-24 outline-none"
                                  data-testid={`gst-item-select-${item.id}`}>
                                  <option value="">Default ({effectiveGst}%)</option>
                                  {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                                  <option value="0">0% (Exempt)</option>
                                </select>
                              </td>
                              <td className="py-1.5 px-3 text-right font-mono text-slate-600">{formatINRFull(gstAmt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Tax-wise summary preview */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="tax-summary-preview">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Tax-wise Subtotals</p>
                    {Object.keys(taxGroups).sort((a, b) => a - b).map(rate => {
                      const g = taxGroups[rate];
                      const halfRate = rate / 2;
                      return (
                        <div key={rate} className="flex justify-between text-xs text-blue-700 py-0.5">
                          <span>GST @{parseFloat(rate)}% (CGST @{halfRate}% + SGST @{halfRate}%)</span>
                          <span className="font-mono">{formatINRFull(g.gst)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* QOT-02: Editable Terms & Conditions */}
                <div data-testid="terms-config-section">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">Terms & Conditions</h3>
                    <button type="button" onClick={() => setTerms([...terms, ''])}
                      className="text-xs text-[#1B5486] hover:underline flex items-center gap-0.5" data-testid="add-term-btn">
                      <Plus size={12} /> Add Clause
                    </button>
                  </div>
                  <div className="space-y-2">
                    {terms.map((t, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <span className="text-xs text-slate-400 mt-2 w-5 shrink-0">{i + 1}.</span>
                        <input value={t} onChange={e => { const u = [...terms]; u[i] = e.target.value; setTerms(u); }}
                          className="flex-1 border border-slate-200 rounded-md px-3 py-1.5 text-xs outline-none focus:border-[#1B5486]"
                          data-testid={`term-input-${i}`} />
                        <button type="button" onClick={() => setTerms(terms.filter((_, j) => j !== i))}
                          className="text-slate-300 hover:text-red-500 mt-1" data-testid={`remove-term-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Closing Text</label>
                    <textarea value={closingText} onChange={e => setClosingText(e.target.value)}
                      rows={2} className="input-field w-full text-xs" data-testid="closing-text-input" />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* QOT-03: Preview */}
        {step === 'preview' && previewUrl && (
          <div className="flex-1 overflow-hidden">
            <iframe src={previewUrl} className="w-full h-full border-0" title="Quotation Preview" data-testid="quotation-preview-iframe" />
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex justify-between gap-3">
          {step === 'preview' ? (
            <>
              <button onClick={() => { setStep('config'); if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
                className="px-4 py-2 border border-slate-200 rounded-md text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1" data-testid="back-to-edit-btn">
                <Edit2 size={14} /> Back to Edit
              </button>
              <button onClick={handleDownload} disabled={generating}
                className="px-5 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-50 flex items-center gap-1" data-testid="download-quotation-btn">
                <Download size={14} /> {generating ? 'Generating...' : 'Download PDF'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <div className="flex gap-2">
                <button onClick={handlePreview} disabled={generating}
                  className="px-4 py-2 border border-[#1B5486] text-[#1B5486] rounded-md text-sm font-medium hover:bg-[#1B5486]/5 disabled:opacity-50 flex items-center gap-1" data-testid="preview-quotation-btn">
                  <Eye size={14} /> {generating ? 'Generating...' : 'Preview'}
                </button>
                <button onClick={handleDownload} disabled={generating}
                  className="px-5 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-50 flex items-center gap-1" data-testid="download-quotation-btn">
                  <Download size={14} /> {generating ? 'Generating...' : 'Download PDF'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineTab({ leadId, activities, canEdit, onUpdate }) {
  const [note, setNote] = useState('');
  const [actType, setActType] = useState('NOTE');

  const addActivity = async () => {
    if (!note.trim()) return;
    await api.post(`/sales/${leadId}/activity`, { description: note, type: actType });
    setNote('');
    onUpdate();
  };

  return (
    <div className="space-y-4" data-testid="timeline-tab">
      {canEdit && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex gap-2 mb-2">
            <select value={actType} onChange={e => setActType(e.target.value)} className="border border-slate-200 rounded-md px-2 py-1.5 text-sm">
              {['NOTE', 'FOLLOW_UP', 'MEETING', 'SITE_VISIT', 'QUOTE_SENT', 'QUOTE_REVISED'].map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note or activity..." data-testid="activity-input"
              className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
            <button onClick={addActivity} data-testid="add-activity-btn" className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm">Add</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {activities.map(a => (
          <div key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
            <div className="w-8 h-8 rounded-full bg-[#1B5486]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Clock size={14} className="text-[#1B5486]" />
            </div>
            <div className="flex-1 bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{formatStatus(a.type)}</span>
                <span className="text-xs text-slate-400">{formatDateTime(a.date || a.created_at)}</span>
              </div>
              <p className="text-sm text-slate-700">{a.description}</p>
              {a.new_status && <p className="text-xs text-slate-500 mt-1">{formatStatus(a.previous_status)} &rarr; <span className={`px-1.5 py-0.5 rounded ${getStatusColor(a.new_status)}`}>{formatStatus(a.new_status)}</span></p>}
            </div>
          </div>
        ))}
        {activities.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No activities yet</div>}
      </div>
    </div>
  );
}
